import { useEffect, useState } from "react";
import DigitalTwinStudio from "./components/DigitalTwinStudio";
import PublicPortal from "./components/portal/PublicPortal";
import EntranceFlash from "./components/portal/EntranceFlash";
import StudioConsole from "./components/StudioConsole";
import OwnerMembershipConsole from "./components/OwnerMembershipConsole";
import { fetchCurrentUser } from "./api/client";
import LoginScreen from "./components/LoginScreen";
import ProfileSetup from "./components/ProfileSetup";
import SiteDesigner from "./components/SiteDesigner";
import { OPERATOR_PATH, PROFILE_PATH, resolveApplicationRoute, resolveStudioEntryView } from "./authRouting";
import { shouldCheckCurrentUser } from "./authGuard";

function NotFoundScreen() {
  return (
    <main className="login-screen">
      <section className="login-card" aria-labelledby="not-found-title">
        <h1 id="not-found-title" className="login-title">Page not found</h1>
        <a className="login-submit" href="/">Return to Studio</a>
      </section>
    </main>
  );
}

function IdentityErrorScreen({ title, description, actionLabel, actionHref }) {
  return (
    <main className="login-screen">
      <section className="login-card" aria-labelledby="identity-error-title">
        <h1 id="identity-error-title" className="login-title">{title}</h1>
        <p className="login-copy">{description}</p>
        {actionHref ? <a className="login-submit" href={actionHref}>{actionLabel}</a> : null}
      </section>
    </main>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [identityError, setIdentityError] = useState("");
  const pathname = window.location.pathname;
  const route = resolveApplicationRoute(pathname);

  useEffect(() => {
    document.title = route === "operator"
      ? "HACaMS Operator Console"
      : route === "profile"
        ? "HACaMS Profile Setup"
        : route === "studio"
          ? "HACaMS™ Smart Studio"
          : route === "not-found"
            ? "Page not found"
            : "HACaMS Login";
  }, [route]);

  useEffect(() => {
    setChecked(false);
    setIdentityError("");
  }, [pathname, route]);

  useEffect(() => {
    if (!shouldCheckCurrentUser(route, pathname)) return undefined;
    let active = true;
    const fallback = route === "studio" ? window.setTimeout(() => { if (active) setChecked(true); }, 3000) : null;

    fetchCurrentUser()
      .then((payload) => {
        if (!active) return;
        window.clearTimeout(fallback);
        setUser(payload.authenticated ? payload.user : null);
        setChecked(true);
      })
      .catch((error) => {
        if (!active) return;
        window.clearTimeout(fallback);
        setUser(null);
        setChecked(true);
        setIdentityError(error?.message || "We couldn't verify your identity right now.");
      });

    return () => {
      active = false;
      window.clearTimeout(fallback);
    };
  }, [pathname, route]);

  if (route === "login") {
    return <LoginScreen />;
  }

  if (route === "studio") {
    // Decide before rendering: otherwise signed-in operators briefly see (and load) the public page.
    if (!checked) return <div className="login-screen login-loading" role="status">Loading…</div>;
    const view = resolveStudioEntryView(user);
    if (view === "console") {
      const roles = new Set((user?.roles || []).map((role) => String(role).toUpperCase()));
      if (roles.has("OWNER") && !roles.has("ADMIN") && !roles.has("PLATFORM_ADMIN")) return <OwnerMembershipConsole user={user} />;
      return <><EntranceFlash /><StudioConsole /></>;
    }
    if (view === "member") return <><EntranceFlash /><DigitalTwinStudio /></>;
    return <PublicPortal />;
  }

  if (route === "operator") {
    if (!checked) return <div className="login-screen login-loading">Loading operator console…</div>;
    if (identityError) {
      return <IdentityErrorScreen
        title="Operator access unavailable"
        description="We couldn't verify your identity right now. Please try again or sign in to continue."
        actionLabel="Retry"
        actionHref="/login?return=%2Foperator"
      />;
    }
    if (!user) {
      window.location.href = `/login?return=${encodeURIComponent(OPERATOR_PATH)}`;
      return null;
    }
    if (resolveStudioEntryView(user) !== "console") {
      window.location.href = "/";
      return null;
    }
    if ((user.roles || []).includes("OWNER") && !(user.roles || []).some((role) => ["ADMIN", "PLATFORM_ADMIN"].includes(String(role).toUpperCase()))) {
      return <OwnerMembershipConsole user={user} />;
    }
    return <StudioConsole />;
  }

  if (route === "profile") {
    if (!checked) return <div className="login-screen login-loading">Loading profile setup…</div>;
    if (identityError) {
      return <IdentityErrorScreen
        title="Profile unavailable"
        description="We couldn't verify your identity right now. Please retry the page or sign in again."
        actionLabel="Retry"
        actionHref="/login?return=%2Fprofile"
      />;
    }
    if (!user) {
      window.location.href = `/login?return=${encodeURIComponent(PROFILE_PATH)}`;
      return null;
    }
    return <ProfileSetup user={user} />;
  }

  if (route === "site-designer") {
    if (!checked) return <div className="login-screen login-loading" role="status">Loading Site Designer…</div>;
    if (identityError || !user) {
      return <IdentityErrorScreen title="Site Designer access unavailable"
        description={identityError || "Sign in with an administrator account to continue."}
        actionLabel="Sign in" actionHref="/login?return=%2Foperator%2Fsite-designer" />;
    }
    if (!(user.roles || []).some((role) => ["ADMIN", "PLATFORM_ADMIN"].includes(String(role).toUpperCase()))) {
      return <IdentityErrorScreen title="Administrator access required"
        description="Site Designer is available to authorized administrators." actionLabel="Operator console" actionHref="/operator" />;
    }
    return <SiteDesigner user={user} />;
  }

  return <NotFoundScreen />;
}
